/**
 * Notification feed — fetch, render, and interactions for the header bell.
 *
 * Server-generated notifications (ending soon, passed your ceiling,
 * opportunity, analysis ready) surface here. Polled on the credit cadence;
 * unread count badges the bell. Clicking an item marks it read and opens the
 * lot URL when present.
 *
 * @module portal/modules/notificationFeed
 */

import { state } from './state';
import { apiUrl, ensureAccessToken } from './api';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:notifications');

export interface FeedNotification {
  id: string;
  kind: string;
  lotId: string;
  lotTitle?: string;
  lotUrl?: string;
  message: string;
  createdAt: string;
  readAt?: string | null;
}

const KIND_ICONS: Record<string, string> = {
  'ending-soon': '⏰',
  'passed-stay-under': '📈',
  'passed-never-exceed': '🛑',
  'passed-user-max': '🛑',
  opportunity: '✅',
  'analysis-ready': '💡',
};

let lastFetchedAt = 0;
let cached: FeedNotification[] = [];
const FETCH_TTL_MS = 60_000;

export async function fetchNotifications(forceRefresh = false): Promise<FeedNotification[]> {
  if (state.mode !== 'auth0') return [];
  const now = Date.now();
  if (!forceRefresh && now - lastFetchedAt < FETCH_TTL_MS) return cached;

  try {
    const token = await ensureAccessToken(false);
    if (!token) return cached;
    const response = await fetch(apiUrl('user/notifications?limit=50'), {
      headers: { Authorization: 'Bearer ' + token },
    });
    if (!response.ok) return cached;
    const data = await response.json();
    cached = Array.isArray(data?.notifications) ? data.notifications : [];
    lastFetchedAt = now;
    updateBellBadge();
    return cached;
  } catch (error) {
    log.warn('Failed to fetch notifications:', error);
    return cached;
  }
}

export function getUnreadCount(): number {
  return cached.filter(n => !n.readAt).length;
}

export async function markNotificationRead(id: string): Promise<void> {
  const local = cached.find(n => n.id === id);
  if (local) local.readAt = new Date().toISOString();
  updateBellBadge();
  try {
    const token = await ensureAccessToken(false);
    if (!token) return;
    await fetch(apiUrl('user/notifications/mark-read'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ id }),
    });
  } catch (error) {
    log.warn('Failed to mark notification read:', error);
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  const nowIso = new Date().toISOString();
  for (const n of cached) {
    if (!n.readAt) n.readAt = nowIso;
  }
  updateBellBadge();
  renderFeedPanel();
  try {
    const token = await ensureAccessToken(false);
    if (!token) return;
    await fetch(apiUrl('user/notifications/mark-read'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ all: true }),
    });
  } catch (error) {
    log.warn('Failed to mark all read:', error);
  }
}

function formatAge(createdAt: string): string {
  const ts = Date.parse(createdAt);
  if (!Number.isFinite(ts)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - ts) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function updateBellBadge(): void {
  const badge = document.getElementById('notification-badge');
  const bell = document.getElementById('notification-bell');
  if (!badge || !bell) return;
  const unread = getUnreadCount();
  if (unread > 0) {
    badge.textContent = unread > 99 ? '99+' : String(unread);
    badge.hidden = false;
    bell.setAttribute('aria-label', `Notifications (${unread} unread)`);
  } else {
    badge.hidden = true;
    bell.setAttribute('aria-label', 'Notifications');
  }
}

function renderFeedPanel(): void {
  const list = document.getElementById('notification-list');
  if (!list) return;

  if (!cached.length) {
    list.innerHTML = '<li class="notification-empty">No notifications yet.</li>';
    return;
  }

  const items = cached
    .map(n => {
      const icon = KIND_ICONS[n.kind] ?? '🔔';
      const unreadClass = n.readAt ? '' : ' notification-item--unread';
      const safeMessage = n.message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return (
        `<li class="notification-item${unreadClass}" data-notification-id="${n.id}"` +
        (n.lotUrl ? ` data-lot-url="${encodeURI(n.lotUrl)}"` : '') +
        ` role="button" tabindex="0">` +
        `<span class="notification-item__icon" aria-hidden="true">${icon}</span>` +
        `<span class="notification-item__message">${safeMessage}</span>` +
        `<span class="notification-item__age">${formatAge(n.createdAt)}</span>` +
        '</li>'
      );
    })
    .join('');
  list.innerHTML = items;
}

function toggleFeedPanel(forceOpen?: boolean): void {
  const panel = document.getElementById('notification-panel');
  const bell = document.getElementById('notification-bell');
  if (!panel || !bell) return;
  const open = forceOpen ?? panel.hidden;
  panel.hidden = !open;
  bell.setAttribute('aria-expanded', String(open));
  if (open) {
    renderFeedPanel();
    void fetchNotifications(true).then(renderFeedPanel);
  }
}

/** Wire bell + panel interactions; call once during boot. */
export function setupNotificationFeed(): void {
  const bell = document.getElementById('notification-bell');
  const panel = document.getElementById('notification-panel');
  const markAll = document.getElementById('notification-mark-all');
  const list = document.getElementById('notification-list');
  if (!bell || !panel || !list) return;

  bell.addEventListener('click', () => toggleFeedPanel());

  markAll?.addEventListener('click', e => {
    e.stopPropagation();
    void markAllNotificationsRead();
  });

  const activateItem = (target: HTMLElement) => {
    const item = target.closest('[data-notification-id]');
    if (!(item instanceof HTMLElement)) return;
    const id = item.getAttribute('data-notification-id');
    if (id) void markNotificationRead(id);
    item.classList.remove('notification-item--unread');
    const lotUrl = item.getAttribute('data-lot-url');
    if (lotUrl) window.open(lotUrl, '_blank', 'noopener');
  };

  list.addEventListener('click', e => {
    if (e.target instanceof HTMLElement) activateItem(e.target);
  });
  list.addEventListener('keydown', e => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target instanceof HTMLElement) {
      e.preventDefault();
      activateItem(e.target);
    }
  });

  // Close when clicking outside
  document.addEventListener('click', e => {
    if (panel.hidden) return;
    const target = e.target;
    if (target instanceof Node && !panel.contains(target) && !bell.contains(target)) {
      toggleFeedPanel(false);
    }
  });

  // Show the bell only when signed in; initial fetch happens via polling.
  refreshBellVisibility();
}

export function refreshBellVisibility(): void {
  const wrapper = document.getElementById('notification-bell-wrapper');
  if (!wrapper) return;
  wrapper.hidden = state.mode !== 'auth0';
}

/** Polling entry point — piggybacks on the credit polling cadence. */
export function pollNotifications(): void {
  if (state.mode !== 'auth0') return;
  refreshBellVisibility();
  void fetchNotifications(false);
}
